import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Send, Settings, BookOpen, Zap, CreditCard, Gift, ArrowRight, MessageCircle, Wifi, WifiOff, X, Phone, CheckCircle, Link2, Copy, Check, ShoppingBag, MoreHorizontal, ExternalLink, Trash2, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { theme } from '../styles/theme';
import { Spinner, ModalOverlay as UIModalOverlay, ModalBox as UIModalBox, ModalHeader as UIModalHeader, ModalTitle as UIModalTitle, ModalBody as UIModalBody, ModalFooter as UIModalFooter, Button, Label, Input, PasswordInput, FormGroup } from '../components/ui';
import { useToast } from '../components/Toast';
import UsageMeter from '../components/billing/UsageMeter';
import UpgradeBanner from '../components/billing/UpgradeBanner';
import VisualScheduler from '../components/VisualScheduler';
import { cronToDescription, nextScheduledAt } from '../utils/cron-helpers';
import api from '../services/api';
import { usePolling } from '../hooks/usePolling';
import BrandIcon from '../components/BrandIcon';
import { mergeEnabledActions, cleanupAfterRemoval } from '../utils/enabled-actions';

const API_URL = process.env.REACT_APP_API_URL;

// Conectores relevantes para B2C — anchor mapeia para a aba/seção na página de config
const CONNECTOR_ICONS = {
  whatsapp:        '💬',
  google_calendar: '📅',
  notion:          '📝',
  telegram:        '✈️',
  internal_chat:   '💭',
  email:           '📧',
  instagram:       '📸',
  webchat:         '💻',
};

export default function B2CDashboard({ tenantId, tenant, agents, tierObj, onRefresh }) {
  const { t, i18n } = useTranslation(['common', 'scheduling']);
  const navigate = useNavigate();
  const toast = useToast();

  const [threads, setThreads]       = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [usage, setUsage]           = useState(null);
  const [credits, setCredits]       = useState(null);
  const [loaded, setLoaded]         = useState(false);
  const [chatInput, setChatInput]   = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [agentTyping, setAgentTyping] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatOpen, setChatOpen]     = useState(false);
  const [lastPoll, setLastPoll]     = useState(null);
  const [waTemplate, setWaTemplate]   = useState(null);
  const [waModal, setWaModal]       = useState(false);
  const [waPhone, setWaPhone]       = useState('');
  const [waSaving, setWaSaving]     = useState(false);
  const [waSuccess, setWaSuccess]   = useState(false);
  const [waError, setWaError]       = useState('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [showSwapAgent, setShowSwapAgent] = useState(false);
  const [swapAgents, setSwapAgents] = useState([]);
  const [swapSelected, setSwapSelected] = useState(null);
  const [swapLoading, setSwapLoading] = useState(false);
  const [referralBannerDismissed, setReferralBannerDismissed] = useState(
    () => !!localStorage.getItem(`referral_banner_dismissed_${tenantId}`)
  );
  const [guideCardDismissed, setGuideCardDismissed] = useState(
    () => !!localStorage.getItem(`guide_card_dismissed_${tenantId}`)
  );
  const [slugModal, setSlugModal]   = useState(false);
  const [slugAgent, setSlugAgent]   = useState('');
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugError, setSlugError]   = useState('');
  // Modal de edição do agente
  const [agentModal, setAgentModal]       = useState(false);
  const [agentModalTab, setAgentModalTab] = useState('identity'); // 'identity' | 'integrations'
  const [agentForm, setAgentForm]         = useState({});
  const [agentSaving, setAgentSaving]     = useState(false);
  const [agentError, setAgentError]       = useState('');
  // Dados de integrações — carregados lazy ao abrir a aba
  const [ctypes, setCtypes]               = useState([]);
  const [agentConns, setAgentConns]       = useState([]);
  const [ctypesLoaded, setCtypesLoaded]   = useState(false);
  const [connModal, setConnModal]         = useState(null); // connector type selecionado
  const [connForm, setConnForm]           = useState({});
  const [connSaving, setConnSaving]       = useState(false);
  const [connError, setConnError]         = useState('');
  const [connSaved, setConnSaved]         = useState(false); // true após salvar — exibe etapa OAuth
  // ── Scheduling section state ──
  const [cronConnectors, setCronConnectors] = useState([]);
  const [scheduledTasks, setScheduledTasks] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleModal, setScheduleModal] = useState(false);
  const [scheduleObj, setScheduleObj] = useState(null);
  const [scheduleCreating, setScheduleCreating] = useState(false);
  const [showScheduleErrors, setShowScheduleErrors] = useState(false);
  const messagesEndRef = useRef(null);

  const agent = agents?.[0] || null;

  const tenantSlug = tenant?.publicSlug || tenantId;
  const publicLink = agent?.publicSlug
    ? `${window.location.origin}/u/${tenantSlug}/${agent.publicSlug}`
    : null;

  const openSlugModal = () => {
    setSlugAgent(agent?.publicSlug || agent?.instanceName?.toLowerCase().replace(/\s+/g, '-') || '');
    setSlugError('');
    setSlugModal(true);
  };

  const openAgentModal = () => {
    setAgentForm({
      instanceName: agent?.instanceName || agent?.name || '',
      customPrompt: agent?.basePrompt
        ? agent?.systemPrompt?.replace(agent.basePrompt, '').replace(/^\n+/, '').trim()
        : agent?.systemPrompt || '',
    });
    setAgentError('');
    setAgentModalTab('identity');
    setAgentModal(true);
  };

  const loadIntegrations = async () => {
    if (ctypesLoaded) return;
    try {
      const [ctRes, connRes] = await Promise.all([
        api.get('/marketplace/connector-types'),
        api.get(`/connectors/${tenantId}`),
      ]);
      // Filtra tipos relevantes para B2C — oculta internos
      // Filtra por audience: exibe 'all' e 'personal', oculta 'business' e system=true
      setCtypes((ctRes.data.connectorTypes || []).filter(ct =>
        ct.enabled !== false &&
        !ct.system &&
        (ct.audience === 'all' || ct.audience === 'personal' || !ct.audience)
      ));
      const all = connRes.data.connectors || [];
      setAgentConns(all.filter(c => c.agentInstanceId === (agent?.instanceId || agent?.agentId)));
      setCtypesLoaded(true);
    } catch { /* silencioso */ }
  };

  const switchTab = (tab) => {
    setAgentModalTab(tab);
    if (tab === 'integrations') loadIntegrations();
  };

  const openConnModalByType = async (connType, existingConn) => {
    // Se ctypes já carregados, usa direto
    let types = ctypes;
    if (!ctypesLoaded) {
      await loadIntegrations();
      const res = await api.get('/marketplace/connector-types').catch(() => ({ data: { connectorTypes: [] } }));
      types = res.data.connectorTypes || [];
    }
    const ct = types.find(c => c.type === connType);
    if (!ct) return;
    const initial = { name: existingConn?.name || ct.name || connType, enabled: existingConn?.enabled !== false };
    (ct.configFields || []).forEach(f => {
      initial[f.name] = existingConn?.[f.name] ?? (f.default !== undefined ? f.default : f.type === 'boolean' ? false : '');
    });
    setConnForm(initial);
    setConnError('');
    setConnSaved(false);
    setConnModal({ ct, existing: existingConn || null });
  };

  const updateEnabledActions = async (agentId, connectorType, existingConnector) => {
    try {
      const agentRes = await api.get(`/marketplace/tenants/${tenantId}/agents/${agentId}`);
      const currentActions = agentRes.data.enabledActions || [];
      const merged = mergeEnabledActions(currentActions, connectorType, existingConnector);
      await api.put(`/marketplace/tenants/${tenantId}/agents/${agentId}`, {
        enabledActions: merged,
      });
    } catch (err) {
      console.error('Failed to update enabledActions (non-fatal):', err.message);
    }
  };

  const saveConnector = async () => {
    if (!connModal) return;
    setConnSaving(true);
    setConnError('');
    try {
      const { ct, existing } = connModal;
      const agentId = agent?.instanceId || agent?.agentId;
      const connectorId = existing?.connectorId || `${ct.type}_${Date.now()}`;
      const payload = { connectorId, type: ct.type, agentInstanceId: agentId, ...connForm, enabled: true };
      if (existing) await api.put(`/connectors/${tenantId}/${connectorId}`, payload);
      else          await api.post(`/connectors/${tenantId}`, payload);
      // Recarrega conectores do agente
      const res = await api.get(`/connectors/${tenantId}`);
      const updated = (res.data.connectors || []).filter(c => c.agentInstanceId === agentId);
      setAgentConns(updated);
      setConnectors(res.data.connectors || []);
      // Auto-enable tools for this connector (fire-and-forget)
      updateEnabledActions(agentId, ct, existing);
      // Tipos que precisam de etapa OAuth após salvar
      const OAUTH_TYPES = ['google_workspace', 'microsoft_graph', 'alexa'];
      if (OAUTH_TYPES.includes(ct.type)) {
        // Atualiza existing com o conector recém-salvo para ter o connectorId correto
        const saved = updated.find(c => c.type === ct.type);
        setConnModal(prev => ({ ...prev, existing: saved || prev.existing }));
        setConnSaved(true);
      } else {
        setConnModal(null);
      }
    } catch (e) {
      setConnError(e.response?.data?.error || e.response?.data?.message || t('personal.errSave', { defaultValue: 'Erro ao salvar.' }));
    } finally {
      setConnSaving(false);
    }
  };

  const removeConnector = async (conn) => {
    try {
      await api.delete(`/connectors/${tenantId}/${conn.connectorId}`);
      const remaining = agentConns.filter(c => c.connectorId !== conn.connectorId);
      setAgentConns(remaining);

      // Clean up enabledActions (fire-and-forget)
      try {
        const agentId = agent?.instanceId || agent?.agentId;
        const removedCt = ctypes.find(t => t.type === conn.type);
        const agentRes = await api.get(`/marketplace/tenants/${tenantId}/agents/${agentId}`);
        const currentActions = agentRes.data.enabledActions || [];
        const cleaned = cleanupAfterRemoval(currentActions, removedCt, remaining, ctypes);
        await api.put(`/marketplace/tenants/${tenantId}/agents/${agentId}`, {
          enabledActions: cleaned,
        });
      } catch (err) {
        console.error('Failed to cleanup enabledActions after removal (non-fatal):', err.message);
      }
    } catch { /* silencioso */ }
  };

  const saveAgent = async () => {
    setAgentSaving(true);
    setAgentError('');
    try {
      await api.put(`/marketplace/tenants/${tenantId}/agents/${agent.instanceId || agent.agentId}`, {
        instanceName: agentForm.instanceName,
        customPrompt: agentForm.customPrompt,
      });
      setAgentModal(false);
      if (onRefresh) onRefresh();
    } catch (e) {
      setAgentError(e.response?.data?.message || e.response?.data?.error || t('personal.errSave', { defaultValue: 'Erro ao salvar.' }));
    } finally {
      setAgentSaving(false);
    }
  };

  const openSwapAgent = async () => {
    setShowSwapAgent(true);
    setSwapSelected(null);
    try {
      const res = await fetch(`${API_URL}/public/agents?audience=b2c`);
      const data = await res.json();
      setSwapAgents((data.agents || []).filter(a => a.agentId !== agent?.agentId));
    } catch {
      setSwapAgents([]);
    }
  };

  const handleSwapAgent = async () => {
    if (!swapSelected) return;
    setSwapLoading(true);
    try {
      // Desinstala o agente atual
      if (agent) {
        await api.delete(`/marketplace/tenants/${tenantId}/agents/${agent.instanceId || agent.agentId}`);
      }
      // Instala o novo
      await api.post(`/marketplace/tenants/${tenantId}/agents/install`, {
        agentId: swapSelected.agentId,
        instanceName: swapSelected.name,
      });
      setShowSwapAgent(false);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setSwapLoading(false);
    }
  };

  const saveSlug = async () => {
    if (!slugAgent.trim()) {
      setSlugError(t('personal.errSlugRequired', { defaultValue: 'Preencha o nome do agente na URL.' }));
      return;
    }
    setSlugSaving(true);
    setSlugError('');
    try {
      await api.put(`/marketplace/tenants/${tenantId}/agents/${agent.instanceId || agent.agentId}`, { agentPublicSlug: slugAgent });
      setSlugModal(false);
      if (onRefresh) onRefresh();
    } catch (e) {
      setSlugError(e.response?.data?.message || e.response?.data?.error || t('personal.errSlugSave', { defaultValue: 'Erro ao salvar. Tente outro nome.' }));
    } finally {
      setSlugSaving(false);
    }
  };

  const removeSlug = async () => {
    if (!agent) return;
    try {
      await api.put(`/marketplace/tenants/${tenantId}/agents/${agent.instanceId || agent.agentId}`, { agentPublicSlug: null });
      if (onRefresh) onRefresh();
    } catch { /* silencioso */ }
  };

  const dismissReferralBanner = () => {
    localStorage.setItem(`referral_banner_dismissed_${tenantId}`, '1');
    setReferralBannerDismissed(true);
  };

  const dismissGuideCard = () => {
    localStorage.setItem(`guide_card_dismissed_${tenantId}`, '1');
    setGuideCardDismissed(true);
  };

  const copyPublicLink = () => {
    if (!publicLink) return;
    navigator.clipboard.writeText(publicLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  useEffect(() => {
    if (!tenantId) return;
    Promise.all([
      api.get(`/internal-chat/${tenantId}/threads`).catch(() => ({ data: { threads: [] } })),
      api.get(`/connectors/${tenantId}`).catch(() => ({ data: { connectors: [] } })),
      api.get(`/usage/${tenantId}`).catch(() => ({ data: null })),
      api.get(`/billing/${tenantId}/credits`).catch(() => ({ data: null })),
      api.get('/connectors/templates').catch(() => ({ data: { templates: [] } })),
    ]).then(([threadsRes, connRes, usageRes, creditsRes, templatesRes]) => {
      setThreads(threadsRes.data.threads || []);
      setConnectors(connRes.data.connectors || []);
      setUsage(usageRes.data);
      setCredits(creditsRes.data);
      // Find WhatsApp template from templates list
      const templates = templatesRes.data.templates || templatesRes.data || [];
      const waTpl = templates.find(tpl => tpl.type === 'whatsapp');
      setWaTemplate(waTpl || null);
      setLoaded(true);

      // Fetch scheduling data (cron connectors + scheduled tasks)
      const allConnectors = connRes.data.connectors || [];
      setCronConnectors(allConnectors.filter(c => c.type === 'cron'));

      setScheduleLoading(true);
      api.get(`/tasks/${tenantId}`)
        .then(tasksRes => {
          const now = new Date().toISOString();
          const pending = (tasksRes.data.tasks || []).filter(
            t => t.status === 'pending' && t.scheduledAt && t.scheduledAt > now
          );
          setScheduledTasks(pending);
        })
        .catch(() => setScheduleError(t('scheduling:error.fetchFailed', { defaultValue: 'Erro ao carregar tarefas agendadas' })))
        .finally(() => setScheduleLoading(false));
    });
  }, [tenantId]);

  // Auto-dismiss do GuideCard quando há evidência de uso anterior
  useEffect(() => {
    if (guideCardDismissed) return;
    if ((usage?.conversations > 0) || (credits?.creditsUsedThisCycle > 0) || threads.length > 0) {
      dismissGuideCard();
    }
  }, [usage, credits, threads, guideCardDismissed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Polling do chat inline com backoff exponencial e Page Visibility API
  const doPoll = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('idToken');
      const res = await fetch(
        `${API_URL}/internal-chat/${tenantId}/poll${lastPoll ? `?since=${lastPoll}` : ''}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const data = await res.json();
      if (data.agentReply) {
        setAgentTyping(false);
        setChatMessages(prev => [...prev, { from: 'agent', text: data.agentReply.message, time: fmtTime(data.agentReply.createdAt) }]);
        setLastPoll(data.agentReply.createdAt);
        return true;  // há novos dados — reseta backoff
      }
      return false;   // sem novos dados — incrementa backoff
    } catch { return false; }
  }, [tenantId, lastPoll]); // eslint-disable-line

  usePolling(doPoll, {
    baseInterval: 3000,
    enabled:      chatOpen && !!agent,
    backoff:      true,
    maxInterval:  30000,
    pauseHidden:  true,
  });

  const sendChat = async () => {
    if (!chatInput.trim() || chatSending || !agent) return;
    const text = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { from: 'me', text, time: fmtTime() }]);
    setChatSending(true);
    setAgentTyping(true);
    try {
      const token = sessionStorage.getItem('idToken');
      await fetch(`${API_URL}/internal-chat/${tenantId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ recipientId: agent.instanceId || agent.agentId, recipientType: 'agent', message: text }),
      });
    } catch {
      setAgentTyping(false);
    }
    finally { setChatSending(false); }
  };

  const openChat = () => {
    if (!chatOpen && agent && chatMessages.length === 0) {
      setChatMessages([{ from: 'agent', text: t('personal.chatGreeting', { name: agent.name, defaultValue: `Olá! Sou ${agent.name}. Como posso te ajudar?` }), time: fmtTime() }]);
    }
    setChatOpen(v => !v);
  };

  const openWaModal = async () => {
    setWaModal(true);
    setWaPhone(waInstancedConnector?.allowedDestinations?.[0] ? `+${waInstancedConnector.allowedDestinations[0]}` : '');
    setWaSuccess(false);
    setWaError('');
    if (!waTemplate) {
      try {
        const res = await api.get('/connectors/templates');
        const templates = res.data.templates || res.data || [];
        const wa = templates.find(tpl => tpl.type === 'whatsapp');
        setWaTemplate(wa || null);
      } catch { setWaTemplate(null); }
    }
  };

  const connectWhatsapp = async () => {
    if (!waPhone.trim() || !waTemplate || !agent) return;
    setWaSaving(true);
    setWaError('');
    try {
      if (waInstancedConnector) {
        // Atualizar allowedDestinations do conector existente
        await api.put(`/connectors/${tenantId}/${waInstancedConnector.connectorId}`, {
          allowedDestinations: [waPhone.replace(/\D/g, '')],
        });
      } else {
        // Criar novo conector instanciado
        await api.post(`/connectors/${tenantId}`, {
          sourceTemplateId: waTemplate.templateId,
          agentInstanceId: agent.instanceId || agent.agentId,
          allowedDestinations: [waPhone.replace(/\D/g, '')],
        });
      }
      setWaSuccess(true);
      const connRes = await api.get(`/connectors/${tenantId}`);
      setConnectors(connRes.data.connectors || []);
    } catch (err) {
      setWaError(err.response?.data?.error || t('personal.errConnect', { defaultValue: 'Erro ao conectar. Tente novamente.' }));
    } finally {
      setWaSaving(false);
    }
  };

  // Conector instanciado de WhatsApp (criado a partir de template)
  const waInstancedConnector = connectors.find(c =>
    c.sourceTemplateId && c.type === 'whatsapp'
  );
  const waChannelPhone = waInstancedConnector?.displayPhone
    || waTemplate?.displayPhone
    || waTemplate?.phoneNumber
    || waTemplate?.name
    || null;

  // Conectores do agente instalado que ainda não foram configurados (enabled: false)
  // Filtra pelo agentInstanceId do agente ativo para não misturar com outros agentes
  const agentInstanceId = agent?.instanceId || agent?.agentId;
  const pendingConnectors = connectors.filter(c =>
    !c.enabled &&
    c.fromTemplate &&
    (!agentInstanceId || c.agentInstanceId === agentInstanceId)
  ).map(c => ({
    connectorId: c.connectorId,
    type:        c.type,
    label:       c.name || c.type,
    icon:        CONNECTOR_ICONS[c.type] || '🔌',
  }));

  // Clique em conector pendente: whatsapp abre modal; demais vão para agents-config com âncora
  const handleConnectorClick = (c) => {
    if (c.type === 'whatsapp') return openWaModal();
    const existing = connectors.find(conn => conn.connectorId === c.connectorId);
    openConnModalByType(c.type, existing);
  };

  // externalConfigured: qualquer conector de canal enabled (exclui system connectors)
  const SYSTEM_CONNECTOR_TYPES = ['internal_chat', 'memory', 'cron', 'task_engine'];
  const externalConfigured = connectors.filter(c => c.enabled && !SYSTEM_CONNECTOR_TYPES.includes(c.type)).length;

  // Determina o estado de onboarding
  const onboardingState = !loaded ? null
    : !agent                                          ? 'no_agent'
    : externalConfigured === 0 && threads.length === 0 ? 'no_connector'
    : threads.length === 0 && !guideCardDismissed      ? 'no_conversation'
    : null; // tudo ok, mostra dashboard normal

  // Últimas conversas (threads do internal chat)
  const recentThreads = threads.slice(0, 4);

  const usagePercent = credits
    ? (credits.totalCreditsAvailable > 0
      ? Math.min(((credits.creditsUsedThisCycle || 0) / credits.totalCreditsAvailable) * 100, 100)
      : 0)
    : (usage?.usagePercent ?? 0);
  const creditsUsed = credits?.creditsUsedThisCycle ?? 0;
  const creditsTotal = credits?.totalCreditsAvailable ?? null;
  const conversations = usage?.conversations ?? 0;

  const showReferralBanner = !referralBannerDismissed && (creditsUsed >= 50 || conversations >= 10) && !onboardingState;

  // ── Scheduling: merge + sort connectors and tasks by next execution (5.4) ──
  const mergedSchedules = [
    ...cronConnectors.map(c => ({
      id: c.connectorId,
      kind: 'connector',
      name: c.name || t('scheduling:sectionTitle', { defaultValue: 'Automações agendadas' }),
      description: c.cronObjective || '',
      frequency: c.cronExpression ? cronToDescription(c.cronExpression, (key, opts) => t(`scheduling:${key}`, opts), tenant?.timezone) : '',
      nextExec: c.cronExpression ? nextScheduledAt(c.cronExpression) : null,
      enabled: c.enabled !== false,
      raw: c,
    })),
    ...scheduledTasks.map(task => ({
      id: task.taskId,
      kind: 'task',
      name: task.title || task.objective || '',
      description: task.objective || '',
      frequency: t('scheduling:preset.once', { defaultValue: 'Uma vez' }),
      nextExec: task.scheduledAt || null,
      enabled: true,
      raw: task,
    })),
  ].sort((a, b) => {
    if (!a.nextExec && !b.nextExec) return 0;
    if (!a.nextExec) return 1;
    if (!b.nextExec) return -1;
    return a.nextExec.localeCompare(b.nextExec);
  });

  // ── Scheduling handlers (5.7, 5.8, 5.9, 5.10) ──
  const handleCreateSchedule = async () => {
    if (scheduleCreating) return;
    if (!scheduleObj || !scheduleObj.isValid) {
      setShowScheduleErrors(true);
      // Show specific validation error
      if (!scheduleObj?.type) {
        toast.error(t('scheduling:error.frequencyRequired', { defaultValue: 'Selecione uma frequência' }));
      } else if (!scheduleObj?.agentInstanceId) {
        toast.error(t('scheduling:error.agentRequired', { defaultValue: 'Selecione um agente' }));
      } else if (!scheduleObj?.objective?.trim() && scheduleObj?.type) {
        toast.error(t('scheduling:error.objectiveRequired', { defaultValue: 'Descreva o objetivo da automação' }));
      } else if (scheduleObj?.type === 'one-shot' && !scheduleObj?.scheduledAt) {
        toast.error(t('scheduling:error.frequencyRequired', { defaultValue: 'Selecione data e horário' }));
      } else {
        toast.error(t('scheduling:error.invalidExpression', { defaultValue: 'Verifique os campos do agendamento' }));
      }
      return;
    }
    setScheduleCreating(true);
    try {
      const agentId = scheduleObj.agentInstanceId || agent?.instanceId || agent?.agentId;
      if (scheduleObj.type === 'recurring') {
        const connectorId = `cron_${Date.now()}`;
        await api.post(`/connectors/${tenantId}`, {
          connectorId,
          type: 'cron',
          name: scheduleObj.objective?.slice(0, 80) || t('scheduling:sectionTitle', { defaultValue: 'Automação agendada' }),
          cronExpression: scheduleObj.cronExpression,
          cronObjective: scheduleObj.objective || '',
          agentInstanceId: agentId,
          enabled: true,
        });
        // Refresh connectors
        const res = await api.get(`/connectors/${tenantId}`);
        const allConns = res.data.connectors || [];
        setConnectors(allConns);
        setCronConnectors(allConns.filter(c => c.type === 'cron'));
      } else if (scheduleObj.type === 'one-shot') {
        await api.post(`/tasks/${tenantId}`, {
          scheduledAt: scheduleObj.scheduledAt,
          source: 'manual',
          title: scheduleObj.objective?.slice(0, 80) || t('scheduling:preset.once', { defaultValue: 'Tarefa agendada' }),
          agentId: agentId,
          objective: scheduleObj.objective || '',
        });
        // Refresh tasks
        const tasksRes = await api.get(`/tasks/${tenantId}`);
        const now = new Date().toISOString();
        setScheduledTasks(
          (tasksRes.data.tasks || []).filter(
            tk => tk.status === 'pending' && tk.scheduledAt && tk.scheduledAt > now
          )
        );
      }
      setScheduleModal(false);
      setScheduleObj(null);
    } catch (e) {
      setScheduleError(e.response?.data?.error || e.response?.data?.message || t('personal.errCreateSchedule', { defaultValue: 'Erro ao criar agendamento.' }));
    } finally {
      setScheduleCreating(false);
    }
  };

  const handleToggleConnector = async (connector) => {
    const newEnabled = !connector.enabled;
    try {
      await api.put(`/connectors/${tenantId}/${connector.connectorId}`, { enabled: newEnabled });
      setCronConnectors(prev =>
        prev.map(c => c.connectorId === connector.connectorId ? { ...c, enabled: newEnabled } : c)
      );
    } catch { /* toast error — silent for now */ }
  };

  const handleDeleteConnector = async (connector) => {
    if (!window.confirm(t('scheduling:deleteConfirm', { defaultValue: 'Tem certeza que deseja excluir esta automação?' }))) return;
    try {
      await api.delete(`/connectors/${tenantId}/${connector.connectorId}`);
      setCronConnectors(prev => prev.filter(c => c.connectorId !== connector.connectorId));
      setConnectors(prev => prev.filter(c => c.connectorId !== connector.connectorId));
    } catch { /* silent */ }
  };

  const handleCancelTask = async (task) => {
    if (!window.confirm(t('scheduling:cancelConfirm', { defaultValue: 'Tem certeza que deseja cancelar esta tarefa agendada?' }))) return;
    try {
      await api.put(`/tasks/${tenantId}/${task.taskId}`, { status: 'cancelled', resultSummary: 'Cancelado pelo usuário' });
      setScheduledTasks(prev => prev.filter(tk => tk.taskId !== task.taskId));
    } catch { /* silent */ }
  };

  return (
    <Wrap>
      <UpgradeBanner tenantId={tenantId} />

      {/* ── Guia de onboarding contextual ── */}
      {onboardingState === 'no_agent' && (
        <GuideCard $variant="primary">
          <GuideEmoji>🤖</GuideEmoji>
          <GuideBody>
            <GuideTitle>{t('personal.guide.noAgentTitle', { defaultValue: 'Escolha seu assistente pessoal' })}</GuideTitle>
            <GuideSub>{t('personal.guide.noAgentSub', { defaultValue: 'Selecione um assistente do marketplace e comece a usar em menos de 2 minutos.' })}</GuideSub>
          </GuideBody>
          <GuideBtn onClick={() => navigate(`/agents-config/${tenantId}`)}>
            {t('personal.guide.noAgentCta', { defaultValue: 'Escolher assistente' })} <ArrowRight size={14} />
          </GuideBtn>
        </GuideCard>
      )}

      {onboardingState === 'no_connector' && (
        waInstancedConnector ? (
          // WhatsApp já conectado via template instanciado: mostra o número do canal
          <GuideCard $variant="secondary">
            <GuideEmoji>✅</GuideEmoji>
            <GuideBody>
              <GuideTitle>{t('personal.guide.waActiveTitle', { defaultValue: 'WhatsApp conectado!' })}</GuideTitle>
              <GuideSub>
                {t('personal.guide.waActiveSub', { defaultValue: 'Envie uma mensagem para o número abaixo e seu assistente responderá.' })}
              </GuideSub>
              {waChannelPhone && (
                <WaChannelPill>
                  <Phone size={13} />
                  {waChannelPhone}
                </WaChannelPill>
              )}
            </GuideBody>
            <GuideBtnSecondary onClick={openChat}>
              {t('personal.guide.orChat', { defaultValue: 'Ou use o chat aqui' })}
            </GuideBtnSecondary>
          </GuideCard>
        ) : (
          // Ainda não conectou: CTA de conexão
          <GuideCard $variant="secondary">
            <GuideEmoji>💬</GuideEmoji>
            <GuideBody>
              <GuideTitle>{t('personal.guide.noConnectorTitle', { defaultValue: 'Conecte o WhatsApp para falar de onde estiver' })}</GuideTitle>
              <GuideSub>{t('personal.guide.noConnectorSub', { defaultValue: 'Seu assistente já está ativo. Conecte o WhatsApp ou use o chat aqui mesmo.' })}</GuideSub>
            </GuideBody>
            <GuideActions>
              <GuideBtn onClick={openWaModal}>
                {t('personal.guide.noConnectorCta', { defaultValue: 'Conectar WhatsApp' })} <ArrowRight size={14} />
              </GuideBtn>
              <GuideBtnSecondary onClick={openChat}>
                {t('personal.guide.orChat', { defaultValue: 'Ou use o chat aqui' })}
              </GuideBtnSecondary>
            </GuideActions>
          </GuideCard>
        )
      )}

      {/* ── Modal WhatsApp Template Instanciation ── */}
      {waModal && (
        <UIModalOverlay onClick={() => !waSaving && setWaModal(false)}>
          <UIModalBox onClick={e => e.stopPropagation()}>
            <UIModalHeader>
              <UIModalTitle>💬 {waInstancedConnector
                ? t('personal.wa.titleEdit', { defaultValue: 'Alterar número conectado' })
                : t('personal.wa.title',     { defaultValue: 'Conectar WhatsApp' })}
              </UIModalTitle>
              <ModalClose onClick={() => setWaModal(false)} disabled={waSaving}><X size={18} /></ModalClose>
            </UIModalHeader>

            {waSuccess ? (
              <ModalSuccess>
                <CheckCircle size={40} color={theme.colors.success} />
                <ModalSuccessTitle>{t('personal.wa.successTitle', { defaultValue: 'WhatsApp conectado!' })}</ModalSuccessTitle>
                <ModalSuccessText>
                  {t('personal.wa.successText', {
                    phone: waTemplate?.displayPhone || waTemplate?.phoneNumber || '',
                    defaultValue: 'A partir de agora, envie mensagens para o número {{phone}} e seu assistente responderá automaticamente.',
                  })}
                </ModalSuccessText>
                {(waTemplate?.displayPhone || waTemplate?.phoneNumber) && (
                  <WaNumberBadge>
                    <Phone size={14} />
                    {waTemplate.displayPhone || waTemplate.phoneNumber}
                  </WaNumberBadge>
                )}
                <GuideBtn onClick={() => setWaModal(false)} style={{ marginTop: 8 }}>
                  {t('personal.wa.done', { defaultValue: 'Entendido!' })}
                </GuideBtn>
              </ModalSuccess>
            ) : (
              <>
                <UIModalBody>
                  {/* Número do canal — destaque visual antes do formulário */}
                  {waTemplate?.displayPhone || waTemplate?.phoneNumber ? (
                    <WaChannelBanner>
                      <WaChannelBannerLabel>
                        {t('personal.wa.connectingTo', { defaultValue: 'Você está se conectando ao número:' })}
                      </WaChannelBannerLabel>
                      <WaChannelBannerPhone>
                        📱 {waTemplate.displayPhone || waTemplate.phoneNumber}
                      </WaChannelBannerPhone>
                      <WaChannelBannerSub>
                        {t('personal.wa.bannerSub', { defaultValue: 'Mensagens enviadas para este número serão respondidas pelo seu assistente.' })}
                      </WaChannelBannerSub>
                    </WaChannelBanner>
                  ) : !waTemplate ? (
                    <ModalHint $warn>{t('personal.wa.noConnector', { defaultValue: 'Nenhum canal WhatsApp disponível no momento.' })}</ModalHint>
                  ) : null}
                  <ModalLabel>{waInstancedConnector
                    ? t('personal.wa.phoneLabelEdit', { defaultValue: 'Novo número de WhatsApp' })
                    : t('personal.wa.phoneLabel',     { defaultValue: 'Seu número de WhatsApp' })}
                  </ModalLabel>
                  <ModalDesc style={{ marginBottom: 4 }}>
                    {waInstancedConnector
                      ? t('personal.wa.descEdit', { defaultValue: 'O número atual será substituído. Apenas o novo número poderá conversar com seu assistente.' })
                      : t('personal.wa.desc',     { defaultValue: 'Informe o número que enviará mensagens. Apenas este número poderá conversar com seu assistente via WhatsApp.' })}
                  </ModalDesc>
                  <ModalInput
                    type="tel"
                    placeholder="+55 11 99999-9999"
                    value={waPhone}
                    onChange={e => setWaPhone(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && connectWhatsapp()}
                    disabled={waSaving}
                    autoFocus
                  />
                  {waError && <ModalError>{waError}</ModalError>}
                </UIModalBody>
                <UIModalFooter>
                  <GuideBtnSecondary onClick={() => setWaModal(false)} disabled={waSaving}>
                    {t('common.cancel', { defaultValue: 'Cancelar' })}
                  </GuideBtnSecondary>
                  <GuideBtn onClick={connectWhatsapp} disabled={waSaving || !waPhone.trim() || !waTemplate}>
                    {waSaving ? <Spinner size="14px" /> : waInstancedConnector
                      ? t('personal.wa.update', { defaultValue: 'Atualizar número' })
                      : t('personal.wa.connect', { defaultValue: 'Conectar' })}
                  </GuideBtn>
                </UIModalFooter>
              </>
            )}
          </UIModalBox>
        </UIModalOverlay>
      )}

      {onboardingState === 'no_conversation' && (
        <GuideCard $variant="accent">
          <GuideEmoji>✨</GuideEmoji>
          <GuideBody>
            <GuideTitle>{t('personal.guide.noConvTitle', { defaultValue: 'Faça sua primeira pergunta' })}</GuideTitle>
            <GuideSuggestions>
              {[
                t('personal.guide.suggestion1', { defaultValue: '"Qual meu compromisso amanhã?"' }),
                t('personal.guide.suggestion2', { defaultValue: '"Resuma este documento"' }),
                t('personal.guide.suggestion3', { defaultValue: '"Me lembre de ligar às 15h"' }),
              ].map((s, i) => (
                <GuideSuggestion key={i} onClick={() => { openChat(); setChatInput(s.replace(/"/g, '')); }}>
                  {s}
                </GuideSuggestion>
              ))}
            </GuideSuggestions>
          </GuideBody>
          <GuideBtn onClick={openChat}>
            {t('personal.openChat', { defaultValue: 'Abrir chat' })} <ArrowRight size={14} />
          </GuideBtn>
          <DismissBtn onClick={dismissGuideCard} aria-label={t('common:close')}>
            <X size={16} />
          </DismissBtn>
        </GuideCard>
      )}

      {/* ── Hero ── */}
      <HeroCard>
        <HeroLeft>
          <AgentEmoji>{agent?.icon || '🤖'}</AgentEmoji>
          <HeroText>
            <HeroTitle>
              {agent
                ? t('personal.heroActive', { name: agent.name, defaultValue: `Seu assistente {{name}} está ativo` })
                : t('personal.heroNoAgent', { defaultValue: 'Configure seu assistente pessoal' })}
            </HeroTitle>
            {agent ? (
              <HeroChannels>
                {waChannelPhone && (
                  <HeroChannel onClick={openWaModal} title={t('personal.waConnected', { defaultValue: 'WhatsApp conectado' })}>
                    <span>💬</span> {waChannelPhone}
                  </HeroChannel>
                )}
                {connectors.filter(c => c.enabled && !SYSTEM_CONNECTOR_TYPES.includes(c.type) && c.type !== 'whatsapp').map(c => (
                  <HeroChannel key={c.connectorId} onClick={() => openConnModalByType(c.type, c)}>
                    <span>{c.type === 'telegram' ? '✈️' : c.type === 'email' ? '📧' : c.type === 'webchat' ? '💻' : '🔌'}</span>
                    {c.name || c.type}
                  </HeroChannel>
                ))}
                {!waChannelPhone && externalConfigured === 0 && (
                  <HeroChannelAdd onClick={openWaModal}>
                    + {t('personal.addWhatsapp', { defaultValue: 'Conectar WhatsApp' })}
                  </HeroChannelAdd>
                )}
              </HeroChannels>
            ) : (
              <HeroSub>{t('personal.heroSubNoAgent', { defaultValue: 'Instale um agente do marketplace para começar' })}</HeroSub>
            )}
          </HeroText>
        </HeroLeft>
        <HeroActions>
          {agent ? (
            <>
              <HeroSecondaryBtn onClick={openAgentModal}>
                <Settings size={15} />
                {t('personal.configAgent', { defaultValue: 'Configurar' })}
              </HeroSecondaryBtn>
              <ChatToggleBtn onClick={openChat} $open={chatOpen}>
                <MessageCircle size={16} />
                {chatOpen
                  ? t('personal.closeChat', { defaultValue: 'Fechar chat' })
                  : t('personal.openChat', { defaultValue: 'Conversar agora' })}
              </ChatToggleBtn>
            </>
          ) : (
            <SetupBtn onClick={() => navigate(`/agents-config/${tenantId}`)}>
              <Settings size={16} />
              {t('personal.setup', { defaultValue: 'Configurar assistente' })}
            </SetupBtn>
          )}
          <StatusDot $online={!!agent} title={agent ? t('personal.online', { defaultValue: 'Online' }) : t('personal.offline', { defaultValue: 'Offline' })}>
            {agent ? <Wifi size={12} /> : <WifiOff size={12} />}
            {agent ? t('personal.online', { defaultValue: 'Online' }) : t('personal.offline', { defaultValue: 'Offline' })}
          </StatusDot>
        </HeroActions>
      </HeroCard>

      {/* ── Card link público ── */}
      {agent && (
        <ShareCard>
          <ShareLeft>
            <ShareIcon><Link2 size={18} /></ShareIcon>
            <ShareText>
              <ShareTitle>{t('personal.shareTitle', { defaultValue: 'Compartilhe seu assistente' })}</ShareTitle>
              {publicLink
                ? <ShareUrl>{publicLink.replace('https://', '')}</ShareUrl>
                : <ShareSetup onClick={openSlugModal}>
                    {t('personal.shareSetup', { defaultValue: '+ Definir link público' })}
                  </ShareSetup>
              }
            </ShareText>
          </ShareLeft>
          {publicLink ? (
            <ShareActions>
              <ShareBtn onClick={copyPublicLink}>
                {copiedLink ? <Check size={14} color={theme.colors.success} /> : <Copy size={14} />}
                {copiedLink ? t('personal.copied', { defaultValue: 'Copiado!' }) : t('personal.copyLink', { defaultValue: 'Copiar' })}
              </ShareBtn>
              <ShareBtn as="a" href={publicLink} target="_blank" rel="noreferrer" title={t('personal.openNewTab', { defaultValue: 'Abrir em nova aba' })}>
                <ExternalLink size={14} />
              </ShareBtn>
              <ShareBtn onClick={openSlugModal} title={t('personal.editLink', { defaultValue: 'Editar link' })}>
                <Settings size={14} />
              </ShareBtn>
              <ShareBtnDanger onClick={removeSlug} title={t('personal.removeLink', { defaultValue: 'Remover link' })}>
                <Trash2 size={14} />
              </ShareBtnDanger>
            </ShareActions>
          ) : (
            <ShareBtn onClick={openSlugModal}>
              {t('personal.shareDefine', { defaultValue: 'Definir' })} <ArrowRight size={14} />
            </ShareBtn>
          )}
        </ShareCard>
      )}

      {/* ── Seção de Automações Agendadas (5.5) ── */}
      {agent && (
        <ScheduleSection>
          <ScheduleSectionHeader>
            <ScheduleSectionTitle>
              {t('scheduling:sectionTitle', { defaultValue: 'Automações agendadas' })}
            </ScheduleSectionTitle>
            <ScheduleNewBtn onClick={() => { setScheduleError(''); setScheduleObj(null); setShowScheduleErrors(false); setScheduleModal(true); }}>
              {t('scheduling:newAutomation', { defaultValue: '+ Nova automação' })}
            </ScheduleNewBtn>
          </ScheduleSectionHeader>

          {scheduleLoading && (
            <ScheduleLoadingWrap>
              <Spinner size="18px" />
            </ScheduleLoadingWrap>
          )}

          {scheduleError && !scheduleModal && (
            <ScheduleErrorMsg>{scheduleError}</ScheduleErrorMsg>
          )}

          {!scheduleLoading && mergedSchedules.length === 0 && (
            <ScheduleEmpty>
              <ScheduleEmptyIcon>📋</ScheduleEmptyIcon>
              <ScheduleEmptyText>
                {t('scheduling:emptyState', { defaultValue: 'Nenhuma automação agendada. Crie uma para automatizar tarefas recorrentes.' })}
              </ScheduleEmptyText>
            </ScheduleEmpty>
          )}

          {!scheduleLoading && mergedSchedules.length > 0 && (
            <ScheduleList>
              {mergedSchedules.map(item => (
                <TaskCardWrap key={item.id} $muted={!item.enabled}>
                  <TaskCardIcon>{item.kind === 'connector' ? '🔄' : '📌'}</TaskCardIcon>
                  <TaskCardBody>
                    <TaskCardName>{item.name}</TaskCardName>
                    {item.description && <TaskCardDesc>{item.description}</TaskCardDesc>}
                    <TaskCardMeta>
                      <TaskCardFreq>{item.frequency}</TaskCardFreq>
                      {item.nextExec && (
                        <TaskCardNext>
                          {new Date(item.nextExec).toLocaleString(i18n.language || 'pt-BR', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </TaskCardNext>
                      )}
                    </TaskCardMeta>
                  </TaskCardBody>
                  <TaskCardActions>
                    {item.kind === 'connector' && (
                      <>
                        <TaskCardToggle
                          $active={item.enabled}
                          onClick={() => handleToggleConnector(item.raw)}
                          title={item.enabled ? t('personal.deactivate', { defaultValue: 'Desativar' }) : t('personal.activate', { defaultValue: 'Ativar' })}
                        >
                          <TaskCardToggleThumb $active={item.enabled} />
                        </TaskCardToggle>
                        <TaskCardDeleteBtn onClick={() => handleDeleteConnector(item.raw)} title={t('common:delete')}>
                          <Trash2 size={14} />
                        </TaskCardDeleteBtn>
                      </>
                    )}
                    {item.kind === 'task' && (
                      <TaskCardCancelBtn onClick={() => handleCancelTask(item.raw)}>
                        <X size={14} /> {t('common:cancel', { defaultValue: 'Cancelar' })}
                      </TaskCardCancelBtn>
                    )}
                  </TaskCardActions>
                </TaskCardWrap>
              ))}
            </ScheduleList>
          )}
        </ScheduleSection>
      )}

      {/* ── Modal de criação de agendamento (5.6) ── */}
      {scheduleModal && (
        <UIModalOverlay onClick={() => !scheduleCreating && setScheduleModal(false)}>
          <UIModalBox maxWidth="560px" onClick={e => e.stopPropagation()}>
            <UIModalHeader>
              <UIModalTitle>📅 {t('scheduling:newAutomation', { defaultValue: 'Nova automação' })}</UIModalTitle>
              <ModalClose onClick={() => setScheduleModal(false)} disabled={scheduleCreating}><X size={18} /></ModalClose>
            </UIModalHeader>
            <UIModalBody>
              <VisualScheduler
                onScheduleChange={setScheduleObj}
                agents={agents || []}
                autoSelectAgent={true}
                showObjective={true}
                timezone={tenant?.timezone}
                showErrors={showScheduleErrors}
              />
              {scheduleError && <ScheduleModalError>{scheduleError}</ScheduleModalError>}
            </UIModalBody>
            <UIModalFooter>
              <GuideBtnSecondary onClick={() => setScheduleModal(false)} disabled={scheduleCreating}>
                {t('scheduling:cancelSchedule', { defaultValue: 'Cancelar' })}
              </GuideBtnSecondary>
              <GuideBtn
                onClick={handleCreateSchedule}
                disabled={scheduleCreating}
              >
                {scheduleCreating ? <Spinner size="14px" /> : t('scheduling:createSchedule', { defaultValue: 'Criar agendamento' })}
              </GuideBtn>
            </UIModalFooter>
          </UIModalBox>
        </UIModalOverlay>
      )}

      {/* ── Mini-modal: definir link público ── */}
      {slugModal && (
        <UIModalOverlay onClick={() => !slugSaving && setSlugModal(false)}>
          <UIModalBox onClick={e => e.stopPropagation()}>
            <UIModalHeader>
              <UIModalTitle>🔗 {t('personal.slugModalTitle', { defaultValue: 'Definir link público' })}</UIModalTitle>
              <ModalClose onClick={() => setSlugModal(false)} disabled={slugSaving}><X size={18} /></ModalClose>
            </UIModalHeader>
            <UIModalBody>
              <ModalDesc>
                {t('personal.slugModalDesc', { defaultValue: 'Crie um link único para qualquer pessoa conversar com seu assistente, sem precisar de conta.' })}
              </ModalDesc>

              {/* Slug do agente */}
              <div>
                <ModalLabel>{t('personal.slugAgent', { defaultValue: 'Nome deste agente na URL' })}</ModalLabel>
                <SlugInputRow>
                  <SlugPrefix style={{ fontSize: 10, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    .../{tenantSlug}/
                  </SlugPrefix>
                  <SlugInput
                    value={slugAgent}
                    onChange={e => setSlugAgent(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40))}
                    placeholder={agent?.instanceName?.toLowerCase().replace(/\s+/g, '-') || 'assistente'}
                  />
                </SlugInputRow>
                <SlugHint>{t('personal.slugAgentHint', { defaultValue: 'Identifica este agente. Ex: recepcao, financeiro.' })}</SlugHint>
              </div>

              {/* Preview */}
              {slugAgent && (
                <SlugPreview>
                  <Link2 size={12} />
                  <span>libbenagents.com/u/{tenantSlug}/{slugAgent}</span>
                </SlugPreview>
              )}

              {slugError && <ModalError>{slugError}</ModalError>}
            </UIModalBody>
            <UIModalFooter>
              <GuideBtnSecondary onClick={() => setSlugModal(false)} disabled={slugSaving}>
                {t('common.cancel', { defaultValue: 'Cancelar' })}
              </GuideBtnSecondary>
              <GuideBtn
                onClick={saveSlug}
                disabled={slugSaving || !slugAgent.trim()}
              >
                {slugSaving ? <Spinner size="14px" /> : t('common.save', { defaultValue: 'Salvar' })}
              </GuideBtn>
            </UIModalFooter>
          </UIModalBox>
        </UIModalOverlay>
      )}

      {/* ── Modal edição do agente ── */}
      {agentModal && (
        <UIModalOverlay onClick={() => !agentSaving && !connSaving && setAgentModal(false)}>
          <UIModalBox maxWidth="480px" onClick={e => e.stopPropagation()}>
            <UIModalHeader>
              <UIModalTitle>{agent?.icon || '🤖'} {t('personal.editAgentTitle', { defaultValue: 'Personalizar assistente' })}</UIModalTitle>
              <ModalClose onClick={() => setAgentModal(false)}><X size={18} /></ModalClose>
            </UIModalHeader>

            {/* Abas */}
            <AgentModalTabs>
              <AgentModalTab $active={agentModalTab === 'identity'} onClick={() => switchTab('identity')}>
                {t('personal.tabIdentity', { defaultValue: 'Assistente' })}
              </AgentModalTab>
              <AgentModalTab $active={agentModalTab === 'integrations'} onClick={() => switchTab('integrations')}>
                {t('personal.tabIntegrations', { defaultValue: 'Integrações' })}
                {agentConns.length > 0 && <AgentModalTabBadge>{agentConns.length}</AgentModalTabBadge>}
              </AgentModalTab>
            </AgentModalTabs>

            {/* Aba: Assistente */}
            {agentModalTab === 'identity' && (
              <>
                <UIModalBody>
                  <div>
                    <ModalLabel>{t('personal.agentName', { defaultValue: 'Nome do assistente' })}</ModalLabel>
                    <ModalInput
                      value={agentForm.instanceName}
                      onChange={e => setAgentForm(f => ({ ...f, instanceName: e.target.value }))}
                      placeholder={agent?.name || 'Sofia'}
                      autoFocus
                    />
                  </div>
                  <div>
                    <ModalLabel>{t('personal.agentPrompt', { defaultValue: 'Instruções personalizadas' })}</ModalLabel>
                    <ModalDesc style={{ marginBottom: 6 }}>
                      {t('personal.agentPromptHint', { defaultValue: 'Diga como seu assistente deve se comportar.' })}
                    </ModalDesc>
                    <ModalTextarea
                      rows={5}
                      value={agentForm.customPrompt}
                      onChange={e => setAgentForm(f => ({ ...f, customPrompt: e.target.value }))}
                      placeholder={t('personal.agentPromptPlaceholder', { defaultValue: 'Ex: Você é meu assistente pessoal. Responda sempre de forma direta e objetiva.' })}
                    />
                  </div>
                  {agentError && <ModalError>{agentError}</ModalError>}
                </UIModalBody>
                <UIModalFooter>
                  <AgentModalFooterLink onClick={() => { setAgentModal(false); navigate(`/agents-config/${tenantId}`); }}>
                    {t('personal.advancedConfig', { defaultValue: 'Configurações avançadas →' })}
                  </AgentModalFooterLink>
                  <GuideBtn onClick={saveAgent} disabled={agentSaving || !agentForm.instanceName?.trim()}>
                    {agentSaving ? <Spinner size="14px" /> : t('common.save', { defaultValue: 'Salvar' })}
                  </GuideBtn>
                </UIModalFooter>
              </>
            )}

            {/* Aba: Integrações */}
            {agentModalTab === 'integrations' && (
              <>
                <UIModalBody style={{ maxHeight: 420, overflowY: 'auto' }}>
                  {!ctypesLoaded ? (
                    <div style={{ textAlign: 'center', padding: '24px 0', color: theme.colors.textMuted }}>
                      <Spinner size="20px" />
                    </div>
                  ) : (
                    <IntegrationGrid>
                      {ctypes.map(ct => {
                        const active = agentConns.find(c => c.type === ct.type);
                        const label = typeof ct.name === 'object' ? (ct.name['pt-BR'] || ct.name['en'] || ct.type) : ct.name;
                        const desc  = typeof ct.description === 'object' ? (ct.description['pt-BR'] || ct.description['en'] || '') : (ct.description || '');
                        return (
                          <IntegrationCard key={ct.type} $active={!!active} onClick={() => openConnModalByType(ct.type, active || null)}>
                            <IntegrationIcon>{ct.icon || '🔌'}</IntegrationIcon>
                            <IntegrationInfo>
                              <IntegrationName>{label}</IntegrationName>
                              <IntegrationDesc>{desc.slice(0, 60)}{desc.length > 60 ? '...' : ''}</IntegrationDesc>
                            </IntegrationInfo>
                            <IntegrationStatus $active={!!active}>
                              {active ? '✓' : '+'}
                            </IntegrationStatus>
                          </IntegrationCard>
                        );
                      })}
                    </IntegrationGrid>
                  )}
                </UIModalBody>
              </>
            )}
          </UIModalBox>
        </UIModalOverlay>
      )}

      {/* ── Modal: trocar assistente ── */}
      {showSwapAgent && (
        <UIModalOverlay onClick={() => !swapLoading && setShowSwapAgent(false)}>
          <UIModalBox maxWidth="520px" onClick={e => e.stopPropagation()}>
            <UIModalHeader>
              <UIModalTitle>
                <RefreshCw size={16} /> {t('personal.swapAgentTitle', { defaultValue: 'Trocar assistente' })}
              </UIModalTitle>
              <ModalClose onClick={() => setShowSwapAgent(false)} disabled={swapLoading}><X size={18} /></ModalClose>
            </UIModalHeader>
            <UIModalBody>
              <ModalDesc>
                {t('personal.swapAgentDesc', { defaultValue: 'Escolha um novo assistente. O atual será substituído.' })}
              </ModalDesc>
              {swapAgents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: theme.colors.textMuted }}>
                  <Spinner size="20px" />
                </div>
              ) : (
                <SwapAgentGrid>
                  {swapAgents.map(a => (
                    <SwapAgentCard
                      key={a.agentId}
                      $selected={swapSelected?.agentId === a.agentId}
                      onClick={() => setSwapSelected(a)}
                    >
                      <SwapAgentIcon><BrandIcon icon={a.icon || '🤖'} size={28} /></SwapAgentIcon>
                      <SwapAgentName>{a.name}</SwapAgentName>
                      <SwapAgentDesc>{a.description || ''}</SwapAgentDesc>
                      {swapSelected?.agentId === a.agentId && (
                        <SwapAgentCheck><Check size={12} /></SwapAgentCheck>
                      )}
                    </SwapAgentCard>
                  ))}
                </SwapAgentGrid>
              )}
            </UIModalBody>
            <UIModalFooter>
              <GuideBtnSecondary onClick={() => setShowSwapAgent(false)} disabled={swapLoading}>
                {t('common.cancel', { defaultValue: 'Cancelar' })}
              </GuideBtnSecondary>
              <GuideBtn onClick={handleSwapAgent} disabled={!swapSelected || swapLoading}>
                {swapLoading ? <Spinner size="14px" /> : t('personal.swapConfirm', { defaultValue: 'Trocar' })}
              </GuideBtn>
            </UIModalFooter>
          </UIModalBox>
        </UIModalOverlay>
      )}

      {/* ── Sub-modal: configurar conector ── */}
      {connModal && (
        <UIModalOverlay onClick={() => !connSaving && setConnModal(null)} style={{ zIndex: 1100 }}>
          <UIModalBox maxWidth="440px" onClick={e => e.stopPropagation()}>
            <UIModalHeader>
              <UIModalTitle>
                {connModal.ct.icon || '🔌'}{' '}
                {typeof connModal.ct.name === 'object' ? (connModal.ct.name['pt-BR'] || connModal.ct.name['en']) : connModal.ct.name}
              </UIModalTitle>
              <ModalClose onClick={() => { setConnModal(null); setConnSaved(false); }} disabled={connSaving}><X size={18} /></ModalClose>
            </UIModalHeader>

            {/* Etapa OAuth — exibida após salvar credenciais */}
            {connSaved ? (
              <>
                <UIModalBody>
                  <OAuthStep>
                    <OAuthStepIcon>✅</OAuthStepIcon>
                    <OAuthStepTitle>
                      {t('personal.connSavedTitle', { defaultValue: 'Credenciais salvas!' })}
                    </OAuthStepTitle>
                    <OAuthStepDesc>
                      {connModal.ct.type === 'google_workspace' && t('personal.oauthGoogleDesc', { defaultValue: 'Agora autorize o acesso à sua conta Google para ativar o conector.' })}
                      {connModal.ct.type === 'microsoft_graph' && t('personal.oauthMsDesc', { defaultValue: 'Agora autorize o acesso à sua conta Microsoft para ativar o conector.' })}
                      {connModal.ct.type === 'alexa' && t('personal.oauthAlexaDesc', { defaultValue: 'Agora vincule seu dispositivo Alexa para ativar o conector.' })}
                    </OAuthStepDesc>
                    {connModal.ct.type === 'google_workspace' && (
                      <OAuthActionBtn onClick={() => {
                        const token = sessionStorage.getItem('idToken') || '';
                        window.location.href = `${process.env.REACT_APP_API_URL}/connectors/${tenantId}/google/auth?token=${encodeURIComponent(token)}`;
                      }}>
                        <span>🔐</span> {t('personal.connectGoogle', { defaultValue: 'Conectar com Google' })}
                      </OAuthActionBtn>
                    )}
                    {connModal.ct.type === 'microsoft_graph' && (
                      <OAuthActionBtn onClick={() => {
                        const token = sessionStorage.getItem('idToken') || '';
                        window.location.href = `${process.env.REACT_APP_API_URL}/connectors/${tenantId}/microsoft/auth?token=${encodeURIComponent(token)}`;
                      }}>
                        <span>🪟</span> {t('personal.connectMicrosoft', { defaultValue: 'Conectar com Microsoft' })}
                      </OAuthActionBtn>
                    )}
                    {connModal.ct.type === 'alexa' && (
                      <OAuthActionBtn onClick={() => { setConnModal(null); setConnSaved(false); navigate(`/agents-config/${tenantId}`); }}>
                        <span>🔵</span> {t('personal.connectAlexa', { defaultValue: 'Vincular Alexa' })}
                      </OAuthActionBtn>
                    )}
                  </OAuthStep>
                </UIModalBody>
                <UIModalFooter>
                  <GuideBtn onClick={() => { setConnModal(null); setConnSaved(false); }}>
                    {t('personal.doLater', { defaultValue: 'Fazer depois' })}
                  </GuideBtn>
                </UIModalFooter>
              </>
            ) : (
              <>
                <UIModalBody>
                  {/* Aviso OAuth antes de preencher — apenas para tipos que precisam */}
                  {(connModal.ct.type === 'google_workspace' || connModal.ct.type === 'microsoft_graph') && connModal.existing?.accessToken && (
                    <OAuthStatusBadge $ok>
                      ✅ {connModal.ct.type === 'google_workspace' ? t('personal.googleAuthorized', { defaultValue: 'Google autorizado' }) : t('personal.microsoftAuthorized', { defaultValue: 'Microsoft autorizado' })}
                      <OAuthReauthBtn onClick={() => {
                        const token = sessionStorage.getItem('idToken') || '';
                        const path = connModal.ct.type === 'google_workspace' ? 'google' : 'microsoft';
                        window.location.href = `${process.env.REACT_APP_API_URL}/connectors/${tenantId}/${path}/auth?token=${encodeURIComponent(token)}`;
                      }}>
                        {t('personal.reauthorize', { defaultValue: 'Reautorizar' })}
                      </OAuthReauthBtn>
                    </OAuthStatusBadge>
                  )}
                  {(connModal.ct.configFields || []).map(f => {
                    const label = typeof f.label === 'object' ? (f.label['pt-BR'] || f.label['en'] || f.name) : (f.label || f.name);
                    const help  = typeof f.helpText === 'object' ? (f.helpText['pt-BR'] || f.helpText['en'] || '') : (f.helpText || '');
                    const ph    = typeof f.placeholder === 'object' ? (f.placeholder['pt-BR'] || f.placeholder['en'] || '') : (f.placeholder || '');

                    if (f.type === 'boolean') return (
                      <ConnFieldRow key={f.name}>
                        <div>
                          <ModalLabel>{label}</ModalLabel>
                          {help && <ModalDesc style={{ marginBottom: 0 }}>{help}</ModalDesc>}
                        </div>
                        <ConnToggle
                          $active={connForm[f.name] !== false}
                          onClick={() => setConnForm(p => ({ ...p, [f.name]: !p[f.name] }))}
                        >
                          <ConnToggleThumb $active={connForm[f.name] !== false} />
                        </ConnToggle>
                      </ConnFieldRow>
                    );

                    if (f.type === 'list') {
                      const items = Array.isArray(connForm[f.name]) ? connForm[f.name] : [];
                      const addLabel = typeof f.addLabel === 'object' ? (f.addLabel['pt-BR'] || f.addLabel['en']) : (f.addLabel || '+ Adicionar');
                      const addItem = () => {
                        const empty = {};
                        (f.itemFields || []).forEach(fi => { empty[fi.name] = ''; });
                        setConnForm(p => ({ ...p, [f.name]: [...items, empty] }));
                      };
                      const removeItem = idx => setConnForm(p => ({ ...p, [f.name]: items.filter((_, i) => i !== idx) }));
                      const updateItem = (idx, key, val) => setConnForm(p => ({
                        ...p, [f.name]: items.map((item, i) => i === idx ? { ...item, [key]: val } : item)
                      }));
                      return (
                        <div key={f.name}>
                          <ModalLabel>{label}{f.required ? ' *' : ''}</ModalLabel>
                          {help && <ConnFieldHint style={{ whiteSpace: 'pre-line', marginBottom: 8 }}>{help}</ConnFieldHint>}
                          {items.map((item, idx) => (
                            <ConnListItem key={idx}>
                              <ConnListIndex>{idx + 1}</ConnListIndex>
                              <ConnListFields>
                                {(f.itemFields || []).map(fi => {
                                  const fiPh = typeof fi.placeholder === 'object' ? (fi.placeholder['pt-BR'] || fi.placeholder['en'] || '') : (fi.placeholder || '');
                                  const fiLb = typeof fi.label === 'object' ? (fi.label['pt-BR'] || fi.label['en'] || fi.name) : (fi.label || fi.name);
                                  return (
                                    <PasswordInput
                                      as={ModalInput}
                                      key={fi.name}
                                      type={fi.type === 'password' ? 'password' : 'text'}
                                      value={item[fi.name] || ''}
                                      onChange={e => updateItem(idx, fi.name, e.target.value)}
                                      placeholder={fiPh || fiLb}
                                      style={{ marginBottom: 4 }}
                                    />
                                  );
                                })}
                              </ConnListFields>
                              <ConnListRemove onClick={() => removeItem(idx)}>×</ConnListRemove>
                            </ConnListItem>
                          ))}
                          <ConnAddItemBtn onClick={addItem}>{addLabel}</ConnAddItemBtn>
                        </div>
                      );
                    }

                    return (
                      <div key={f.name}>
                        <ModalLabel>{label}{f.required ? ' *' : ''}</ModalLabel>
                        <PasswordInput
                          as={ModalInput}
                          type={f.type === 'password' ? 'password' : 'text'}
                          value={connForm[f.name] || ''}
                          onChange={e => setConnForm(p => ({ ...p, [f.name]: e.target.value }))}
                          placeholder={ph}
                        />
                        {help && <ConnFieldHint style={{ whiteSpace: 'pre-line' }}>{help}</ConnFieldHint>}
                      </div>
                    );
                  })}
                  {connModal.existing && (
                    <ConnRemoveBtn onClick={() => { removeConnector(connModal.existing); setConnModal(null); setConnSaved(false); }}>
                      {t('personal.removeIntegration', { defaultValue: 'Remover integração' })}
                    </ConnRemoveBtn>
                  )}
                  {connError && <ModalError>{connError}</ModalError>}
                </UIModalBody>
                <UIModalFooter>
                  <GuideBtnSecondary onClick={() => { setConnModal(null); setConnSaved(false); }} disabled={connSaving}>
                    {t('common.cancel', { defaultValue: 'Cancelar' })}
                  </GuideBtnSecondary>
                  <GuideBtn onClick={saveConnector} disabled={connSaving}>
                    {connSaving ? <Spinner size="14px" /> : t('common.save', { defaultValue: 'Salvar' })}
                  </GuideBtn>
                </UIModalFooter>
              </>
            )}
          </UIModalBox>
        </UIModalOverlay>
      )}

      {/* ── Banner referral — marco de 10 conversas ── */}
      {showReferralBanner && (
        <ReferralBanner>
          <ReferralEmoji>🎉</ReferralEmoji>
          <ReferralBody>
            <ReferralTitle>
              {credits
                ? t('personal.referral.creditsMilestoneTitle', {
                    count: creditsUsed,
                    defaultValue: `Seu assistente já consumiu {{count}} créditos!`,
                  })
                : t('personal.referral.milestoneTitle', {
                    count: conversations,
                    defaultValue: `Seu assistente já respondeu {{count}} conversas!`,
                  })
              }
            </ReferralTitle>
            <ReferralSub>
              {t('personal.referral.milestoneSub', {
                defaultValue: 'Indique um amigo e ambos ganham créditos extras grátis.',
              })}
            </ReferralSub>
          </ReferralBody>
          <ReferralActions>
            <GuideBtn onClick={() => navigate(`/referral/${tenantId}`)}>
              <Gift size={14} />
              {t('personal.referral.cta', { defaultValue: 'Indicar amigos' })}
            </GuideBtn>
            <ReferralDismiss onClick={dismissReferralBanner} title={t('common.dismiss', { defaultValue: 'Dispensar' })}>
              <X size={14} />
            </ReferralDismiss>
          </ReferralActions>
        </ReferralBanner>
      )}

      {/* ── Chat inline ── */}
      {chatOpen && agent && (
        <ChatCard>
          <ChatMessages>
            {chatMessages.map((msg, i) => (
              <ChatMsg key={i} $from={msg.from}>
                <ChatBubble $from={msg.from}>{msg.text}</ChatBubble>
                <ChatTime>{msg.time}</ChatTime>
              </ChatMsg>
            ))}
            {agentTyping && (
              <ChatMsg $from="agent">
                <TypingBubble>
                  <TypingDot /><TypingDot /><TypingDot />
                </TypingBubble>
              </ChatMsg>
            )}
            <div ref={messagesEndRef} />
          </ChatMessages>
          <ChatInputRow>
            <ChatInput
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat()}
              placeholder={t('personal.chatPlaceholder', { defaultValue: 'Digite uma mensagem...' })}
              disabled={chatSending}
              autoFocus
              enterKeyHint="send"
            />
            <SendBtn aria-label={t('common:send')} onClick={sendChat} disabled={chatSending || !chatInput.trim()}>
              {chatSending ? <Spinner size="14px" /> : <Send size={14} />}
            </SendBtn>
          </ChatInputRow>
        </ChatCard>
      )}

      {/* ── Métricas + Conectores ── */}
      <TwoCol $single={pendingConnectors.length === 0}>
        {/* Uso do mês — Créditos */}
        <MetricCard>
          <MetricTitle>{t('personal.usageTitle', { defaultValue: 'Uso este mês' })}</MetricTitle>
          {credits ? (
            <CreditMeterWrap>
              <CreditMeterRow>
                <CreditMeterLabel>{t('personal.creditsUsed', { defaultValue: 'Créditos usados' })}</CreditMeterLabel>
                <CreditMeterValue $color={
                  usagePercent >= 100 ? theme.colors.danger
                  : usagePercent >= 80 ? theme.colors.warning
                  : theme.colors.success
                }>
                  {creditsUsed.toLocaleString('pt-BR')}
                  {creditsTotal != null && ` / ${creditsTotal.toLocaleString('pt-BR')}`}
                </CreditMeterValue>
              </CreditMeterRow>
              <CreditMeterTrack>
                <CreditMeterFill style={{
                  width: `${usagePercent}%`,
                  background: usagePercent >= 100 ? theme.colors.danger
                    : usagePercent >= 80 ? theme.colors.warning
                    : theme.colors.success
                }} />
              </CreditMeterTrack>
              {usagePercent >= 100 && (
                <CreditMeterAlert $color={theme.colors.danger}>
                  {t('personal.creditsExhausted', { defaultValue: '🚨 Créditos esgotados' })}
                </CreditMeterAlert>
              )}
              {usagePercent >= 80 && usagePercent < 100 && (
                <CreditMeterAlert $color={theme.colors.warning}>
                  {t('personal.creditsNearLimit', { percent: Math.round(usagePercent), defaultValue: `⚠️ ${Math.round(usagePercent)}% dos créditos utilizados` })}
                </CreditMeterAlert>
              )}
              <CreditMeterMeta>
                <span>{t('personal.plan', { defaultValue: 'Plano' })}: <strong>{tenant?.tier || '—'}</strong></span>
              </CreditMeterMeta>
            </CreditMeterWrap>
          ) : (
            <UsageMeter
              usage={conversations}
              limit={tierObj?.limits?.conversations ?? null}
              tier={tenant?.tier}
              overage={usage?.overageConversations || 0}
            />
          )}
          <MetricFooter>
            <MetricLink onClick={() => navigate(`/billing/${tenantId}`)}>
              {t('personal.seePlan', { defaultValue: 'Ver plano e faturamento' })} <ArrowRight size={12} />
            </MetricLink>
          </MetricFooter>
        </MetricCard>

        {/* Conectores pendentes de configuração */}
        {pendingConnectors.length > 0 && (
          <MetricCard>
            <MetricTitle>
              {t('personal.connectorsTitle', { defaultValue: 'Conectores' })}
              <ConnectorCount>{pendingConnectors.length} {t('personal.pending', { defaultValue: 'pendentes' })}</ConnectorCount>
            </MetricTitle>
            <ConnectorList>
              {pendingConnectors.map(c => (
                <ConnectorRow key={c.connectorId} $configured={false}
                  onClick={() => handleConnectorClick(c)}>
                  <ConnectorIcon>{c.icon}</ConnectorIcon>
                  <ConnectorLabel>{c.label}</ConnectorLabel>
                  <ConnectorBadge>{t('personal.connect', { defaultValue: 'Configurar' })} →</ConnectorBadge>
                </ConnectorRow>
              ))}
            </ConnectorList>
          </MetricCard>
        )}
      </TwoCol>

      {/* ── Conversas recentes ── */}
      {recentThreads.length > 0 && (
        <SectionCard>
          <SectionHeader>
            <SectionTitle>{t('personal.recentConversations', { defaultValue: 'Conversas recentes' })}</SectionTitle>
            <SectionLink onClick={() => navigate(`/logs/${tenantId}`)}>
              {t('personal.seeAll', { defaultValue: 'Ver todas' })} <ArrowRight size={12} />
            </SectionLink>
          </SectionHeader>
          <ThreadList>
            {recentThreads.map((thread, i) => (
              <ThreadRow key={thread.threadId || i}>
                <ThreadIcon>{agent?.icon || '🤖'}</ThreadIcon>
                <ThreadBody>
                  <ThreadAgent>{thread.agentName || agent?.name || t('personal.assistantFallback', { defaultValue: 'Assistente' })}</ThreadAgent>
                  <ThreadPreview>{thread.lastMessage?.text || '—'}</ThreadPreview>
                </ThreadBody>
                <ThreadTime>{thread.updatedAt ? fmtRelative(thread.updatedAt, i18n.language) : ''}</ThreadTime>
              </ThreadRow>
            ))}
          </ThreadList>
        </SectionCard>
      )}

      {/* ── Ações rápidas ── */}
      <QuickActions>
        {[
          { icon: BookOpen,   label: t('personal.knowledge', { defaultValue: 'O que ele sabe' }),      path: `knowledge/${tenantId}` },
          { icon: Zap,        label: t('personal.tasks',     { defaultValue: 'Tarefas' }),              path: `tasks/${tenantId}` },
        ].map(({ icon: Icon, label, path }) => (
          <QuickBtn key={path} onClick={() => navigate(`/${path}`)}>
            <Icon size={18} strokeWidth={1.6} />
            <span>{label}</span>
          </QuickBtn>
        ))}
        <QuickBtn onClick={openSwapAgent}>
          <RefreshCw size={18} strokeWidth={1.6} />
          <span>{t('personal.swapAgent', { defaultValue: 'Trocar assistente' })}</span>
        </QuickBtn>
        <QuickBtnMore onClick={() => setShowMoreActions(v => !v)}>
          <MoreHorizontal size={18} strokeWidth={1.6} />
          <span>{t('personal.more', { defaultValue: 'Mais' })}</span>
        </QuickBtnMore>
      </QuickActions>

      {showMoreActions && (
        <MoreActionsPanel>
          {[
            { icon: ShoppingBag, label: t('personal.newAgent',  { defaultValue: 'Novo agente' }),            path: `agents-config/${tenantId}` },
            { icon: Gift,        label: t('personal.referral.quickBtn', { defaultValue: 'Indicar amigos' }), path: `referral/${tenantId}` },
          ].map(({ icon: Icon, label, path }) => (
            <MoreActionBtn key={path} onClick={() => { setShowMoreActions(false); navigate(`/${path}`); }}>
              <Icon size={16} strokeWidth={1.6} />
              <span>{label}</span>
            </MoreActionBtn>
          ))}
        </MoreActionsPanel>
      )}
    </Wrap>
  );
}

function fmtTime(iso) {
  return new Date(iso || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function fmtRelative(iso, lang = 'pt-BR') {
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 1)   return lang.startsWith('pt') ? 'agora'         : 'now';
  if (mins < 60)  return lang.startsWith('pt') ? `${mins}min`    : `${mins}m`;
  if (hours < 24) return lang.startsWith('pt') ? `${hours}h`     : `${hours}h`;
  return lang.startsWith('pt') ? `${days}d` : `${days}d`;
}

// ── Styled ────────────────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;

// ── Onboarding Guide ──────────────────────────────────────────────────────────

const guideVariants = {
  primary:   { bg: theme.colors.primaryLight,  border: theme.colors.primaryBorder },
  secondary: { bg: theme.colors.successLight,                  border: theme.colors.successBorder },
  accent:    { bg: theme.colors.warningLight,                  border: theme.colors.warningBorder },
};

const GuideCard = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[5]};
  border-radius: ${theme.radii.xl};
  border: 1.5px solid ${p => guideVariants[p.$variant]?.border || theme.colors.border};
  background: ${p => guideVariants[p.$variant]?.bg || theme.colors.white};
  flex-wrap: wrap;
  animation: ${fadeIn} 0.3s ease;
  @media (max-width: 640px) { flex-direction: column; align-items: flex-start; }
`;
const GuideEmoji = styled.div`font-size: 2.5rem; flex-shrink: 0; line-height: 1;`;
const GuideBody = styled.div`flex: 1; min-width: 0; display: flex; flex-direction: column; gap: ${theme.spacing[2]};`;
const GuideTitle = styled.div`font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const GuideSub = styled.div`font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary}; line-height: 1.5;`;
const GuideActions = styled.div`display: flex; gap: ${theme.spacing[2]}; flex-wrap: wrap;`;
const GuideBtn = styled.button`
  display: inline-flex; align-items: center; gap: ${theme.spacing[1]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${theme.colors.primary}; color: white; border: none;
  border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold}; cursor: pointer; white-space: nowrap; flex-shrink: 0;
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.primaryHover}; }
`;
const GuideBtnSecondary = styled.button`
  display: inline-flex; align-items: center;
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: none; color: ${theme.colors.primary};
  border: 1px solid ${theme.colors.primaryBorder}; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm}; cursor: pointer; white-space: nowrap;
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.primaryLight}; }
`;
const GuideSuggestions = styled.div`display: flex; flex-wrap: wrap; gap: ${theme.spacing[2]};`;
const GuideSuggestion = styled.button`
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: white; border: 1px solid ${theme.colors.warningBorder};
  border-radius: ${theme.radii.full}; font-size: ${theme.typography.xs};
  color: ${theme.colors.textSecondary}; cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover { border-color: ${theme.colors.primary}; color: ${theme.colors.primary}; background: ${theme.colors.primaryLight}; }
`;
const DismissBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textSecondary};
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
`;

const WaChannelPill = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 12px;
  background: ${theme.colors.successLight}; border: 1px solid #86efac;
  border-radius: ${theme.radii.full};
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.success};
  width: fit-content;
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  animation: ${fadeIn} 0.3s ease;
`;

const HeroCard = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const HeroLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

const AgentEmoji = styled.div`
  font-size: 3rem;
  line-height: 1;
  flex-shrink: 0;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
`;

const HeroText = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[1]};`;

const HeroTitle = styled.div`
  font-size: ${theme.typography.xl};
  font-weight: ${theme.typography.bold};
  color: white;
  text-shadow: 0 1px 3px rgba(0,0,0,0.15);
`;

const HeroSub = styled.div`
  font-size: ${theme.typography.sm};
  color: rgba(255,255,255,0.85);
`;

const HeroChannels = styled.div`
  display: flex; flex-wrap: wrap; gap: ${theme.spacing[2]}; margin-top: 2px;
`;

const HeroChannel = styled.button`
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px ${theme.spacing[3]};
  background: rgba(255,255,255,0.22);
  border: 1px solid rgba(255,255,255,0.5);
  border-radius: ${theme.radii.full};
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  color: white; cursor: pointer; white-space: nowrap;
  transition: ${theme.transitions.fast};
  &:hover { background: rgba(255,255,255,0.35); }
`;

const HeroChannelAdd = styled(HeroChannel)`
  background: rgba(255,255,255,0.1);
  border-style: dashed;
  border-color: rgba(255,255,255,0.4);
  font-weight: ${theme.typography.normal};
  &:hover { background: rgba(255,255,255,0.22); border-style: solid; }
`;

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex-shrink: 0;
`;

const ChatToggleBtn = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: ${p => p.$open ? 'rgba(255,255,255,0.2)' : 'white'};
  color: ${p => p.$open ? 'white' : theme.colors.primary};
  border: 2px solid ${p => p.$open ? 'rgba(255,255,255,0.5)' : 'white'};
  border-radius: ${theme.radii.lg};
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover { opacity: 0.9; }
`;

const SetupBtn = styled(ChatToggleBtn)``;

const HeroSecondaryBtn = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: rgba(255,255,255,0.18);
  color: white;
  border: 1.5px solid rgba(255,255,255,0.5);
  border-radius: ${theme.radii.lg};
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover { background: rgba(255,255,255,0.28); }
`;

const StatusDot = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.medium};
  color: ${p => p.$online ? theme.colors.successBorder : 'rgba(255,255,255,0.6)'};
`;

// ── Chat inline ───────────────────────────────────────────────────────────────

const ChatCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xl};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 320px;
  box-shadow: ${theme.shadows.md};
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const ChatMsg = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${p => p.$from === 'me' ? 'flex-end' : 'flex-start'};
`;

const ChatBubble = styled.div`
  max-width: 75%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.radii.lg};
  background: ${p => p.$from === 'me' ? theme.colors.primary : theme.colors.gray100};
  color: ${p => p.$from === 'me' ? 'white' : theme.colors.textPrimary};
  font-size: ${theme.typography.sm};
  line-height: 1.5;
`;

const ChatTime = styled.div`
  font-size: 10px;
  color: ${theme.colors.textMuted};
  margin-top: 2px;
`;

const typingBounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30%            { transform: translateY(-5px); opacity: 1; }
`;

const TypingBubble = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.gray100};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px 12px 12px 2px;
  width: fit-content;
`;

const TypingDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${theme.colors.textMuted};
  animation: ${typingBounce} 1.2s ease infinite;
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.4s; }
`;

const ChatInputRow = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]};
  padding-bottom: max(${theme.spacing[3]}, env(safe-area-inset-bottom));
  border-top: 1px solid ${theme.colors.border};
`;

const ChatInput = styled.input`
  flex: 1;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  font-family: inherit;
  outline: none;
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
`;

const SendBtn = styled.button`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.radii.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${theme.transitions.fast};
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { background: ${theme.colors.primaryHover}; }
`;

// ── Métricas ──────────────────────────────────────────────────────────────────

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$single ? '1fr' : '1fr 1fr'};
  gap: ${theme.spacing[4]};
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const MetricCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const MetricTitle = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MetricFooter = styled.div`margin-top: auto;`;

const MetricLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  font-size: ${theme.typography.xs};
  color: ${theme.colors.primary};
  cursor: pointer;
  padding: 0;
  &:hover { text-decoration: underline; }
`;

// ── Credit Meter (B2C usage card) ─────────────────────────────────────────────

const CreditMeterWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const CreditMeterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CreditMeterLabel = styled.span`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
`;

const CreditMeterValue = styled.span`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${p => p.$color || theme.colors.textPrimary};
`;

const CreditMeterTrack = styled.div`
  height: 8px;
  background: ${theme.colors.gray100};
  border-radius: ${theme.radii.full};
  overflow: hidden;
`;

const CreditMeterFill = styled.div`
  height: 100%;
  border-radius: ${theme.radii.full};
  transition: width 0.4s ease;
`;

const CreditMeterAlert = styled.p`
  font-size: ${theme.typography.xs};
  color: ${p => p.$color};
  margin: 0;
`;

const CreditMeterMeta = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  strong { color: ${theme.colors.textSecondary}; }
`;

// ── Conectores ────────────────────────────────────────────────────────────────

const ConnectorCount = styled.span`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.normal};
  color: ${theme.colors.textMuted};
`;

const ConnectorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const ConnectorRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.radii.md};
  border: 1px solid ${p => p.$configured ? theme.colors.successBorder : theme.colors.border};
  background: ${p => p.$configured ? theme.colors.successLight : theme.colors.gray50};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover { border-color: ${theme.colors.primary}; background: ${theme.colors.primaryLight}; }
`;

const ConnectorIcon = styled.span`font-size: 1rem; flex-shrink: 0;`;

const ConnectorLabel = styled.span`
  flex: 1;
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary};
`;

const ConnectorBadge = styled.span`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  color: ${p => p.$ok ? '#065f46' : theme.colors.primary};
  white-space: nowrap;
`;

// ── Conversas recentes ────────────────────────────────────────────────────────

const SectionCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing[5]};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing[3]};
`;

const SectionTitle = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const SectionLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  font-size: ${theme.typography.xs};
  color: ${theme.colors.primary};
  cursor: pointer;
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const ThreadList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const ThreadRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[2]} ${theme.spacing[1]};
  border-radius: ${theme.radii.md};
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.gray50}; }
`;

const ThreadIcon = styled.div`font-size: 1.25rem; flex-shrink: 0;`;

const ThreadBody = styled.div`flex: 1; min-width: 0;`;

const ThreadAgent = styled.div`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const ThreadPreview = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ThreadTime = styled.div`
  font-size: 10px;
  color: ${theme.colors.textMuted};
  flex-shrink: 0;
`;

// ── Share Card ───────────────────────────────────────────────────────────────

const ShareCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%);
  border: 1.5px solid ${theme.colors.purpleBorder};
  border-radius: ${theme.radii.xl};
  animation: ${fadeIn} 0.3s ease;
  flex-wrap: wrap;
`;

const ShareLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  min-width: 0;
  flex: 1;
`;

const ShareIcon = styled.div`
  width: 36px; height: 36px; border-radius: ${theme.radii.lg};
  background: ${theme.colors.purpleLight}; color: ${theme.colors.purple700};
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
`;

const ShareText = styled.div`
  display: flex; flex-direction: column; gap: 2px; min-width: 0;
`;

const ShareTitle = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.purple900};
`;

const ShareUrl = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.purple700};
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
`;

const ShareSetup = styled.button`
  background: none; border: none; padding: 0;
  font-size: ${theme.typography.xs};
  color: ${theme.colors.purple600}; cursor: pointer;
  font-weight: ${theme.typography.semibold};
  &:hover { text-decoration: underline; }
`;

const SlugInputRow = styled.div`
  display: flex; align-items: stretch; margin-top: 4px;
`;

const SlugPrefix = styled.div`
  display: flex; align-items: center;
  padding: 0 ${theme.spacing[2]};
  background: ${theme.colors.gray100};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md} 0 0 ${theme.radii.md};
  font-size: 11px; color: ${theme.colors.textMuted}; white-space: nowrap;
`;

const SlugInput = styled.input`
  flex: 1; padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: 0 ${theme.radii.md} ${theme.radii.md} 0;
  border-left: none;
  font-size: ${theme.typography.sm}; font-family: monospace; outline: none;
  &:focus { border-color: ${theme.colors.purple600}; box-shadow: 0 0 0 3px ${theme.colors.purpleLight}; }
`;

const SlugHint = styled.div`
  font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: 3px;
`;

const SlugPreview = styled.div`
  display: flex; align-items: center; gap: 6px;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: #f5f3ff; border: 1px solid ${theme.colors.purpleBorder};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs}; font-family: monospace;
  color: ${theme.colors.purple700}; font-weight: ${theme.typography.semibold};
`;

const ShareActions = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]}; flex-shrink: 0;
`;

const ShareBtn = styled.button`
  display: inline-flex; align-items: center; gap: 5px;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: white; color: ${theme.colors.purple700};
  border: 1px solid ${theme.colors.purpleBorder}; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};
  cursor: pointer; white-space: nowrap; text-decoration: none;
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.purpleLight}; border-color: #a78bfa; }
`;

const ShareBtnDanger = styled(ShareBtn)`
  color: ${theme.colors.danger};
  border-color: ${theme.colors.dangerBorder};
  &:hover { background: ${theme.colors.dangerLight}; border-color: ${theme.colors.danger}; }
`;

// ── Ações rápidas ─────────────────────────────────────────────────────────────

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing[3]};
  @media (max-width: 640px) { grid-template-columns: repeat(4, 1fr); }
`;

const QuickBtn = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xl};
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  transition: ${theme.transitions.base};
  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
    box-shadow: ${theme.shadows.md};
    transform: translateY(-2px);
  }
  svg { color: ${theme.colors.textMuted}; }
  &:hover svg { color: ${theme.colors.primary}; }
`;

const QuickBtnMore = styled(QuickBtn)`
  border-style: dashed;
  color: ${theme.colors.textMuted};
  &:hover { border-color: ${theme.colors.primary}; color: ${theme.colors.primary}; }
`;

const MoreActionsPanel = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xl};
  animation: ${fadeIn} 0.15s ease;
`;

const MoreActionBtn = styled(QuickBtn)`
  background: ${theme.colors.white};
`;

// ── Modal WhatsApp ────────────────────────────────────────────────────────────





const ModalClose = styled.button`
  background: none; border: none; cursor: pointer;
  color: ${theme.colors.textMuted}; display: flex; align-items: center;
  padding: 4px; border-radius: ${theme.radii.sm};
  &:hover { color: ${theme.colors.textPrimary}; background: ${theme.colors.gray100}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;


const ModalDesc = styled.p`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
  line-height: 1.6;
  margin: 0;
`;

const SwapAgentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: ${theme.spacing[3]};
  margin-top: ${theme.spacing[4]};
`;

const SwapAgentCard = styled.div`
  position: relative;
  border: 2px solid ${p => p.$selected ? theme.colors.primary : theme.colors.border};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing[3]};
  text-align: center;
  cursor: pointer;
  transition: ${theme.transitions.fast};
  background: ${p => p.$selected ? theme.colors.primaryLight || '#f0f7ff' : 'white'};
  &:hover { border-color: ${theme.colors.primary}; box-shadow: ${theme.shadows.sm}; }
`;

const SwapAgentIcon = styled.div`margin-bottom: ${theme.spacing[1]}; display: flex; justify-content: center;`;
const SwapAgentName = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const SwapAgentDesc = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: 2px; line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;`;
const SwapAgentCheck = styled.div`
  position: absolute; top: 6px; right: 6px;
  width: 20px; height: 20px; border-radius: 50%;
  background: ${theme.colors.primary}; color: white;
  display: flex; align-items: center; justify-content: center;
`;

const ModalLabel = styled.label`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textPrimary};
`;

const ModalInput = styled.input`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  font-family: inherit;
  outline: none;
  width: 100%; box-sizing: border-box;
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
  &:disabled { background: ${theme.colors.gray50}; }
`;

const ModalTextarea = styled.textarea`
  width: 100%; box-sizing: border-box;
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  font-family: inherit; resize: vertical; outline: none;
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
`;

const AgentModalFooterLink = styled.button`
  background: none; border: none; padding: 0;
  font-size: ${theme.typography.xs}; color: ${theme.colors.primary};
  cursor: pointer; text-align: left; flex: 1;
  &:hover { text-decoration: underline; }
`;

const AgentModalTabs = styled.div`
  display: flex; border-bottom: 2px solid ${theme.colors.border};
  padding: 0 ${theme.spacing[5]};
`;

const AgentModalTab = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: none; border: none;
  border-bottom: 3px solid ${p => p.$active ? theme.colors.primary : 'transparent'};
  color: ${p => p.$active ? theme.colors.primary : theme.colors.textSecondary};
  font-size: ${theme.typography.sm};
  font-weight: ${p => p.$active ? theme.typography.semibold : theme.typography.normal};
  cursor: pointer; margin-bottom: -2px;
  transition: ${theme.transitions.fast};
  &:hover { color: ${theme.colors.primary}; }
`;

const AgentModalTabBadge = styled.span`
  background: ${theme.colors.primary}; color: white;
  font-size: 10px; font-weight: 600;
  border-radius: ${theme.radii.full};
  padding: 1px 6px; line-height: 1.4;
`;

const IntegrationGrid = styled.div`
  display: flex; flex-direction: column; gap: ${theme.spacing[2]};
`;

const IntegrationCard = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  border: 1.5px solid ${p => p.$active ? theme.colors.primary : theme.colors.border};
  border-radius: ${theme.radii.lg};
  background: ${p => p.$active ? theme.colors.primaryLight : theme.colors.white};
  cursor: pointer; transition: ${theme.transitions.fast};
  &:hover { border-color: ${theme.colors.primary}; box-shadow: ${theme.shadows.sm}; }
`;

const IntegrationIcon = styled.div`font-size: 1.5rem; flex-shrink: 0; line-height: 1;`;

const IntegrationInfo = styled.div`flex: 1; min-width: 0;`;

const IntegrationName = styled.div`
  font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const IntegrationDesc = styled.div`
  font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};
  line-height: 1.3; margin-top: 1px;
`;

const IntegrationStatus = styled.div`
  width: 24px; height: 24px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700;
  background: ${p => p.$active ? theme.colors.primary : theme.colors.gray100};
  color: ${p => p.$active ? 'white' : theme.colors.textMuted};
`;

const ConnFieldRow = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: ${theme.spacing[4]}; padding: ${theme.spacing[2]} 0;
`;

const ConnFieldHint = styled.div`
  font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: 3px;
`;

const ConnToggle = styled.div`
  width: 40px; height: 22px; border-radius: 11px; flex-shrink: 0;
  background: ${p => p.$active ? theme.colors.primary : theme.colors.border};
  cursor: pointer; position: relative; transition: background 0.2s; margin-top: 2px;
`;

const ConnToggleThumb = styled.div`
  width: 18px; height: 18px; border-radius: 50%; background: white;
  position: absolute; top: 2px;
  left: ${p => p.$active ? '20px' : '2px'};
  transition: left 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.2);
`;

const ConnRemoveBtn = styled.button`
  background: none; border: none; padding: 0;
  font-size: ${theme.typography.xs}; color: ${theme.colors.danger};
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const ConnListItem = styled.div`
  display: flex; align-items: flex-start; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]};
  background: ${theme.colors.gray50}; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md}; margin-bottom: ${theme.spacing[2]};
`;
const ConnListIndex = styled.div`
  width: 20px; height: 20px; border-radius: 50%; flex-shrink: 0; margin-top: 6px;
  background: ${theme.colors.primary}; color: white;
  font-size: 10px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
`;
const ConnListFields = styled.div`flex: 1; display: flex; flex-direction: column; gap: 4px;`;
const ConnListRemove = styled.button`
  background: none; border: none; cursor: pointer; flex-shrink: 0; margin-top: 4px;
  color: ${theme.colors.danger}; font-size: 16px; line-height: 1; padding: 2px 4px;
  &:hover { opacity: 0.7; }
`;
const ConnAddItemBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 100%; padding: ${theme.spacing[2]};
  background: none; border: 1.5px dashed ${theme.colors.primary};
  border-radius: ${theme.radii.md}; color: ${theme.colors.primary};
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};
  cursor: pointer;
  &:hover { background: ${theme.colors.primaryLight}; }
`;

const OAuthStep = styled.div`
  display: flex; flex-direction: column; align-items: center;
  gap: ${theme.spacing[3]}; text-align: center; padding: ${theme.spacing[2]} 0;
`;
const OAuthStepIcon = styled.div`font-size: 2.5rem; line-height: 1;`;
const OAuthStepTitle = styled.div`
  font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;
const OAuthStepDesc = styled.div`
  font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary};
  line-height: 1.5;
`;
const OAuthActionBtn = styled.button`
  display: inline-flex; align-items: center; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: ${theme.colors.primary}; color: white; border: none;
  border-radius: ${theme.radii.lg}; font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold}; cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.primaryHover}; }
`;
const OAuthStatusBadge = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${p => p.$ok ? theme.colors.successLight : theme.colors.warningLight};
  border: 1px solid ${p => p.$ok ? theme.colors.successBorder : theme.colors.warningBorder};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};
  color: ${p => p.$ok ? theme.colors.success : '#92400e'};
`;
const OAuthReauthBtn = styled.button`
  background: none; border: none; padding: 0;
  font-size: ${theme.typography.xs}; color: ${theme.colors.primary};
  cursor: pointer; font-weight: ${theme.typography.semibold};
  &:hover { text-decoration: underline; }
`;

const ModalHint = styled.div`
  font-size: ${theme.typography.xs};
  color: ${p => p.$warn ? theme.colors.warning : theme.colors.textMuted};
  line-height: 1.5;
`;

const ModalError = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.danger};
  background: ${theme.colors.dangerLight};
  border: 1px solid ${theme.colors.dangerBorder};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
`;


const ModalSuccess = styled.div`
  display: flex; flex-direction: column; align-items: center;
  gap: ${theme.spacing[3]}; padding: ${theme.spacing[6]} ${theme.spacing[5]};
  text-align: center;
`;

const ModalSuccessTitle = styled.div`
  font-size: ${theme.typography.lg};
  font-weight: ${theme.typography.bold};
  color: ${theme.colors.textPrimary};
`;

const ModalSuccessText = styled.div`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
  line-height: 1.6;
`;

const WaChannelBanner = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: ${theme.spacing[4]};
  background: ${theme.colors.successLight}; border: 1.5px solid #86efac;
  border-radius: ${theme.radii.xl}; text-align: center;
  margin-bottom: ${theme.spacing[2]};
`;
const WaChannelBannerLabel = styled.div`
  font-size: ${theme.typography.xs}; color: ${theme.colors.success};
  font-weight: ${theme.typography.medium};
`;
const WaChannelBannerPhone = styled.div`
  font-size: ${theme.typography.xl}; font-weight: ${theme.typography.bold};
  color: #14532d; letter-spacing: 0.02em;
`;
const WaChannelBannerSub = styled.div`
  font-size: ${theme.typography.xs}; color: #166534; line-height: 1.4;
`;

const WaNumberBadge = styled.div`
  display: inline-flex; align-items: center; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${theme.colors.successLight}; border: 1px solid #bbf7d0;
  border-radius: ${theme.radii.full};
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.success};
`;

// ── Referral Banner ─────────────────────────────────────────────────────────────────────────────────────

const ReferralBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
  border: 1.5px solid #fde68a;
  border-radius: ${theme.radii.xl};
  animation: ${fadeIn} 0.3s ease;
  flex-wrap: wrap;
`;

const ReferralEmoji = styled.div`font-size: 2rem; flex-shrink: 0; line-height: 1;`;

const ReferralBody = styled.div`
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: ${theme.spacing[1]};
`;

const ReferralTitle = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: #92400e;
`;

const ReferralSub = styled.div`
  font-size: ${theme.typography.xs};
  color: #a16207;
  line-height: 1.5;
`;

const ReferralActions = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]}; flex-shrink: 0;
`;

const ReferralDismiss = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px;
  background: none; border: 1px solid #fcd34d;
  border-radius: ${theme.radii.md};
  color: #a16207; cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover { background: #fef08a; }
`;

// ── Scheduling Section ────────────────────────────────────────────────────────

const ScheduleSection = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const ScheduleSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
`;

const ScheduleSectionTitle = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const ScheduleNewBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  border: 1px solid ${theme.colors.primaryBorder};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.primary}; color: ${theme.colors.white}; }
`;

const ScheduleLoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[6]};
`;

const ScheduleErrorMsg = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.danger};
  background: ${theme.colors.dangerLight};
  border: 1px solid ${theme.colors.dangerBorder};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
`;

const ScheduleModalError = styled(ScheduleErrorMsg)`
  margin-top: ${theme.spacing[2]};
`;

const ScheduleEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[6]} ${theme.spacing[4]};
  text-align: center;
`;

const ScheduleEmptyIcon = styled.div`
  font-size: 2rem;
  line-height: 1;
  color: ${theme.colors.gray300};
`;

const ScheduleEmptyText = styled.div`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textMuted};
  line-height: 1.5;
  max-width: 320px;
`;

const ScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

// ── Task Card ─────────────────────────────────────────────────────────────────

const TaskCardWrap = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg};
  background: ${theme.colors.white};
  transition: ${theme.transitions.fast};
  opacity: ${p => p.$muted ? 0.55 : 1};
  &:hover { border-color: ${theme.colors.borderHover}; box-shadow: ${theme.shadows.sm}; }
`;

const TaskCardIcon = styled.div`
  font-size: 1.25rem;
  line-height: 1;
  flex-shrink: 0;
  margin-top: 2px;
`;

const TaskCardBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TaskCardName = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TaskCardDesc = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TaskCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-top: 2px;
  flex-wrap: wrap;
`;

const TaskCardFreq = styled.span`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.primary};
  font-weight: ${theme.typography.medium};
`;

const TaskCardNext = styled.span`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
`;

const TaskCardActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-shrink: 0;
  margin-top: 2px;
`;

const TaskCardToggle = styled.div`
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: ${p => p.$active ? theme.colors.primary : theme.colors.border};
  cursor: pointer;
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;
`;

const TaskCardToggleThumb = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  position: absolute;
  top: 2px;
  left: ${p => p.$active ? '18px' : '2px'};
  transition: left 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
`;

const TaskCardDeleteBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: none;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  color: ${theme.colors.textMuted};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover { color: ${theme.colors.danger}; border-color: ${theme.colors.dangerBorder}; background: ${theme.colors.dangerLight}; }
`;

const TaskCardCancelBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  background: none;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover { color: ${theme.colors.danger}; border-color: ${theme.colors.dangerBorder}; background: ${theme.colors.dangerLight}; }
`;
